import { useState } from 'react'
import { Check, MoreVertical } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from '@/components/ui/popover'
import { formatVND } from '@/lib/format'
import { twJoin } from '@/lib/utils'
import { m } from '@/lib/paraglide/messages'

interface Props {
    amountDue: number
    customerPayment: number
    onCustomerPaymentChange: (v: number) => void
    overpayAction: 'change' | 'credit'
    onOverpayActionChange: (v: 'change' | 'credit') => void
    hasCustomer: boolean
    paymentMethod?: 'cash' | 'bank_transfer'
    onPaymentMethodChange?: (v: 'cash' | 'bank_transfer') => void
    /** M-175: có `onTransferAmountChange` → nhập tách hai ô Tiền mặt
     *  (`customerPayment`) và Chuyển khoản (`transferAmount`) thay cho một ô
     *  số tiền + chọn một hình thức. */
    transferAmount?: number
    onTransferAmountChange?: (v: number) => void
    /** M-180: có `onCombinedChange` → mặc định một ô số tiền + chọn hình
     *  thức (kiểu KiotViet); nút "⋮" bật "Thanh toán kết hợp" mới hiện hai ô
     *  Tiền mặt / Chuyển khoản. Bấm lại một hình thức là thoát kết hợp. */
    combined?: boolean
    onCombinedChange?: (v: boolean) => void
}

function AmountInput({
    value,
    onChange,
    label,
}: {
    value: number
    onChange: (v: number) => void
    label?: string
}) {
    return (
        <input
            type="number"
            min={0}
            step="any"
            inputMode="decimal"
            aria-label={label}
            value={value || ''}
            onChange={(e) => onChange(Math.max(0, Number(e.target.value)))}
            placeholder="0"
            className="w-44 text-right rounded border px-3 py-2 text-lg font-semibold focus:outline-none focus:ring-1 focus:ring-primary"
        />
    )
}

/** Generate quick amount buttons: exact + round-ups, max 4 unique values > 0 */
function generateQuickAmounts(amountDue: number): number[] {
    if (amountDue <= 0) return []
    const amounts = new Set<number>([amountDue])
    const roundUps = [50_000, 100_000, 200_000, 500_000]
    for (const step of roundUps) {
        const rounded = Math.ceil(amountDue / step) * step
        if (rounded > amountDue) amounts.add(rounded)
    }
    return [...amounts].sort((a, b) => a - b).slice(0, 4)
}

/** Hàng chọn hình thức (M-180, kiểu KiotViet): nút tròn Tiền mặt /
 *  Chuyển khoản, "⋮" mở menu "Thanh toán kết hợp". Đang kết hợp thì không nút
 *  tròn nào được chọn. */
function PaymentMethodPicker({
    method,
    onMethodChange,
    combined,
    onCombinedChange,
}: {
    method: 'cash' | 'bank_transfer' | null
    onMethodChange: (v: 'cash' | 'bank_transfer') => void
    combined: boolean
    onCombinedChange?: (v: boolean) => void
}) {
    const [menuOpen, setMenuOpen] = useState(false)
    const options = [
        { value: 'cash', label: m.payment_method_cash() },
        { value: 'bank_transfer', label: m.payment_method_bank() },
    ] as const

    return (
        <div className="flex items-center gap-6">
            {options.map((o) => (
                <label
                    key={o.value}
                    className="flex items-center gap-2 cursor-pointer"
                >
                    <input
                        type="radio"
                        checked={method === o.value}
                        onChange={() => onMethodChange(o.value)}
                        className="size-5 accent-primary"
                    />
                    <span>{o.label}</span>
                </label>
            ))}
            {onCombinedChange && (
                <Popover open={menuOpen} onOpenChange={setMenuOpen}>
                    <PopoverTrigger asChild>
                        <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            aria-label={m.payment_method_more()}
                            className="ml-auto size-10 cursor-pointer text-primary"
                        >
                            <MoreVertical className="size-5" />
                        </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-56 p-1">
                        <button
                            type="button"
                            aria-pressed={combined}
                            onClick={() => {
                                setMenuOpen(false)
                                onCombinedChange(!combined)
                            }}
                            className="flex w-full min-h-11 cursor-pointer items-center gap-2 rounded-md px-2 py-2 text-left text-sm transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                        >
                            <Check
                                className={twJoin(
                                    'size-4 text-primary',
                                    !combined && 'invisible',
                                )}
                            />
                            {m.payment_method_combined()}
                        </button>
                    </PopoverContent>
                </Popover>
            )}
        </div>
    )
}

export function PosCheckoutPayment({
    amountDue,
    customerPayment,
    onCustomerPaymentChange,
    overpayAction,
    onOverpayActionChange,
    hasCustomer,
    paymentMethod = 'cash',
    onPaymentMethodChange,
    transferAmount = 0,
    onTransferAmountChange,
    combined = false,
    onCombinedChange,
}: Props) {
    const switchable = onCombinedChange !== undefined
    const split =
        onTransferAmountChange !== undefined && (!switchable || combined)
    const tendered = customerPayment + (split ? transferAmount : 0)
    const diff = tendered - amountDue
    // Nút số tiền nhanh điền ô tiền mặt → gợi ý theo phần còn lại sau CK.
    const quickAmounts = generateQuickAmounts(
        split ? Math.max(0, amountDue - transferAmount) : amountDue,
    )
    // Đang kết hợp mà bấm một hình thức → thoát kết hợp rồi chọn hình thức đó.
    const handleMethodChange = (v: 'cash' | 'bank_transfer') => {
        if (split) onCombinedChange?.(false)
        onPaymentMethodChange?.(v)
    }

    return (
        <div className="space-y-3 text-base">
            {/* Số tiền khách trả — một hình thức: một ô dùng chung cho tiền mặt
                lẫn chuyển khoản (M-8), phương thức chỉ nói tiền vào bằng đường
                nào. Kết hợp (M-175): tổng của hai ô bên dưới. */}
            <div className="flex justify-between items-center">
                <span className="text-lg font-semibold">
                    {m.customer_payment()}
                </span>
                {split ? (
                    <span
                        data-testid="pos-tendered-total"
                        className="text-lg font-semibold"
                    >
                        {formatVND(tendered)}
                    </span>
                ) : (
                    <AmountInput
                        label={m.customer_payment()}
                        value={customerPayment}
                        onChange={onCustomerPaymentChange}
                    />
                )}
            </div>

            {(!split || switchable) && (
                <PaymentMethodPicker
                    method={split ? null : paymentMethod}
                    onMethodChange={handleMethodChange}
                    combined={split}
                    onCombinedChange={onCombinedChange}
                />
            )}

            {/* M-175: khách trả kết hợp — mỗi hình thức một ô, tổng thiếu
                thành nợ, dư thì trả lại / ghi số dư. */}
            {split && (
                <>
                    <div className="flex justify-between items-center">
                        <span>{m.payment_method_cash()}</span>
                        <AmountInput
                            label={m.payment_method_cash()}
                            value={customerPayment}
                            onChange={onCustomerPaymentChange}
                        />
                    </div>
                    <div className="flex justify-between items-center">
                        <span>{m.payment_method_bank()}</span>
                        <AmountInput
                            label={m.payment_method_bank()}
                            value={transferAmount}
                            onChange={(v) => onTransferAmountChange?.(v)}
                        />
                    </div>
                </>
            )}

            {/* Nút số tiền nhanh */}
            {quickAmounts.length > 0 && (
                <div className="flex gap-2 flex-wrap">
                    {quickAmounts.map((amt) => (
                        <button
                            key={amt}
                            type="button"
                            onClick={() => onCustomerPaymentChange(amt)}
                            className={`rounded-full border px-4 py-2 text-sm transition-colors ${
                                customerPayment === amt
                                    ? 'border-primary bg-primary/10 text-primary font-medium'
                                    : 'border-border hover:border-primary/50'
                            }`}
                        >
                            {formatVND(amt)}
                        </button>
                    ))}
                </div>
            )}

            {/* Thu thiếu → phần còn lại thành nợ khách */}
            {diff < 0 && (
                <div className="space-y-1">
                    <div className="flex justify-between text-destructive">
                        <span>{m.customer_credit_debt()}</span>
                        <span className="font-medium">
                            - {formatVND(Math.abs(diff))}
                        </span>
                    </div>
                    {!hasCustomer && (
                        <p className="text-xs text-destructive">
                            {m.customer_select_for_debt()}
                        </p>
                    )}
                </div>
            )}

            {/* Thu dư: trả lại tiền thừa hay cộng vào số dư khách */}
            {diff > 0 && (
                <div className="space-y-2">
                    <label className="flex justify-between items-center cursor-pointer">
                        <span className="flex items-center gap-1.5">
                            <input
                                type="radio"
                                checked={overpayAction === 'change'}
                                onChange={() => onOverpayActionChange('change')}
                                className="accent-primary"
                            />
                            {m.payment_change_due()}
                        </span>
                        <span className="font-medium">{formatVND(diff)}</span>
                    </label>
                    <label
                        className={`flex justify-between items-center ${
                            hasCustomer
                                ? 'cursor-pointer'
                                : 'opacity-40 cursor-not-allowed'
                        }`}
                    >
                        <span className="flex items-center gap-1.5">
                            <input
                                type="radio"
                                checked={overpayAction === 'credit'}
                                onChange={() => onOverpayActionChange('credit')}
                                disabled={!hasCustomer}
                                className="accent-primary"
                            />
                            {m.customer_credit_debt()}
                        </span>
                        <span className="font-medium">{formatVND(diff)}</span>
                    </label>
                </div>
            )}
        </div>
    )
}
